import CloseIcon from "@mui/icons-material/Close";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  Theme,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { MediaLibraryDialogView } from "pages/MediaLibraryPage/MediaLibraryDialogView";
import React, { useState, useEffect, useRef } from "react";
import { FormattedMessage as T } from "react-intl";
import { IMediaItem, MediaItemSource } from "services/KnowfullyAdminClient";
import { FileSizes, MediaType } from "utils/mediaUtils";

type SkuCoverImageFieldProps = {
  value?: string;
  onChange?: (mediaLibraryItemId?: string) => void;
  readMode?: boolean;
};

const useStyles = (theme: Theme) => ({
  imageContainer: {
    position: "relative",
    display: "inline-block",
  },
  image: {
    height: 100,
    maxWidth: 200,
    objectFit: "contain",
    borderRadius: 4,
  },
  closeButton: {
    position: "absolute",
    top: 0,
    right: 0,
    transform: "translate(25%, -25%)",
    backgroundColor: theme.palette.error.main,
    color: theme.palette.error.contrastText,
    "&:hover": {
      backgroundColor: theme.palette.error.dark,
    },
  },
  label: {
    color: theme.palette.text.secondary,
  },
});

const SkuCoverImageField: React.FC<SkuCoverImageFieldProps> = ({
  value,
  onChange,
  readMode,
}) => {
  const styles = useStyles(useTheme());
  const [dialogOpen, setDialogOpen] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [currentValue, setCurrentValue] = useState(value);
  const imgRef = useRef<HTMLImageElement | null>(null);

  useEffect(() => {
    if (value !== currentValue) {
      setCurrentValue(value);
      setImageLoaded(false);
    }
  }, [value, currentValue]);

  useEffect(() => {
    if (value && imgRef.current?.complete && imgRef.current.naturalWidth > 0) {
      setImageLoaded(true);
    }
  }, [value]);

  const showSpinner = value && !imageLoaded;
  const showImage = value && imageLoaded;
  const showCloseButton = !readMode && showImage;
  const showAddButton = !readMode && !value;

  if (readMode && !value) {
    return null;
  }

  const handleImageSelect = (mediaItem: IMediaItem) => {
    onChange?.(mediaItem.fileUrl);
    setDialogOpen(false);
  };

  const handleRemoveImage = () => {
    onChange?.(undefined);
    setImageLoaded(true);
  };

  const handleOpenDialog = () => {
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
  };

  const handleImageLoad = () => {
    setImageLoaded(true);
  };

  const handleImageError = () => {
    setImageLoaded(true);
  };

  return (
    <Box>
      {!readMode && (
        <Typography
          variant="subtitle2"
          gutterBottom
          sx={styles.label}
        >
          <T id="skuCoverImageLabel" />
        </Typography>
      )}

      {value && (
        <Box sx={styles.imageContainer}>
          {showSpinner && <CircularProgress size={24} />}
          <Box
            component="img"
            ref={imgRef}
            src={value}
            onLoad={handleImageLoad}
            onError={handleImageError}
            sx={{
              ...styles.image,
              display: showImage ? "block" : "none",
            }}
          />
          {showCloseButton && (
            <IconButton
              size="small"
              onClick={handleRemoveImage}
              sx={styles.closeButton}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
      )}

      {showAddButton && (
        <Button
          variant="contained"
          startIcon={<CloudUploadIcon />}
          onClick={handleOpenDialog}
        >
          <T id="addCoverImageLabel" />
        </Button>
      )}

      <Dialog
        fullWidth
        maxWidth="lg"
        open={dialogOpen}
        onClose={handleCloseDialog}
      >
        <DialogContent>
          <MediaLibraryDialogView
            source={MediaItemSource.MediaLibrary}
            options={[MediaType.IMAGE]}
            maxFileSizeBytes={FileSizes.Kb250}
            onClickMediaLibraryItem={handleImageSelect}
            customBackButtonLabel="backLabel"
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default SkuCoverImageField;
